/**
 * PATH: packages/email-hub/src/feature/components/composeRailEmailQuery.ts
 * FILE_ID: EMAILHUB-COMPOSE-RAIL-QUERY-001
 * PURPOSE: Map the shared inbox search grammar onto the compose-rail list query.
 * WHY: The rail must filter through useEmails, not a second search box or a
 *      client filter of the first 50 rows. Scope tabs and AI review stay off.
 */
import { buildExcludeCategories } from '../lib/categoryConfig'
import { parseEmailSearchQuery } from '../lib/parseEmailSearchQuery'
import { smartSearchRankParam } from '../lib/smartSearchFlag'
import type { EmailListParams } from '../hooks/useEmails.types'
import { splitMailboxKeys } from '../stores/emailFilterStore'

export interface ComposeRailQueryInput {
  debouncedSearch: string
  smartSearchOn: boolean
  selectedCategories: string[]
  hidePromotions: boolean
  mutedCategories: string[]
  allocation?: 'allocated' | 'unallocated'
  hideCleared: boolean
  isRead?: boolean
  isStarred?: boolean
  jobIds: number[]
  mailboxKeys: string[]
  fromAddressContains: string
  receivedAtGte?: string
  receivedAtLte?: string
  hasAttachments?: boolean
  hasCalendarEvent?: boolean
}

export function buildComposeRailEmailQuery(input: ComposeRailQueryInput): EmailListParams {
  const parsed = parseEmailSearchQuery(input.debouncedSearch)
  const mailboxes = splitMailboxKeys(input.mailboxKeys)
  return {
    folder: 'inbox',
    persistKey: 'email:compose-rail',
    page_size: 50,
    category__in: input.selectedCategories.length ? input.selectedCategories : undefined,
    category__not: buildExcludeCategories(
      input.hidePromotions,
      input.selectedCategories,
      input.mutedCategories,
    ),
    search: parsed.freeText || undefined,
    search_rank: smartSearchRankParam(input.smartSearchOn),
    is_read: parsed.isReadFilter ?? input.isRead,
    is_starred: parsed.isStarredFilter ?? input.isStarred,
    allocation: input.allocation,
    hide_cleared:
      input.hideCleared && input.allocation !== 'allocated' ? true : undefined,
    job_id__in: input.jobIds.length ? input.jobIds : undefined,
    synced_by_account_id__in:
      mailboxes.accountIds.length ? mailboxes.accountIds : undefined,
    unattributed_owner_id__in:
      mailboxes.unattributedUserIds.length ? mailboxes.unattributedUserIds : undefined,
    from_address__icontains:
      parsed.fromAddressContains ?? (input.fromAddressContains || undefined),
    received_at__gte: parsed.receivedAtGte ?? input.receivedAtGte,
    received_at__lte: parsed.receivedAtLte ?? input.receivedAtLte,
    has_attachments: parsed.hasAttachments ?? input.hasAttachments,
    has_calendar_event: parsed.hasCalendarEvent ?? input.hasCalendarEvent,
    attachment_filename: parsed.attachmentFilename,
    search_scope: parsed.searchScope,
  }
}
